import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import { canonicalCategory, vendorLabel } from '@/lib/providers/integration-state';
import { PROVIDER_FILTERS, type AttentionItem, type ProviderFilter } from '@/lib/providers/providers-overview';

/**
 * The top of the Proveedores page: five filter pills that are also the
 * environment's summary (how many providers, how many real / sandbox /
 * synthetic, how many off or not configured), and ONE attention line that
 * appears only when something needs looking at. Nothing here repeats a card's
 * own explanation.
 */

/** A small marker per pill, in the mode chip's colour. */
const PILL_DOT: Record<ProviderFilter, string> = {
  all: 'bg-foreground/60',
  real: 'bg-rose-500',
  sandbox: 'bg-amber-500',
  synthetic: 'bg-violet-500',
  off: 'bg-muted-foreground/40',
};

export function FilterPills({
  counts,
  active,
  onPick,
}: {
  counts: Record<ProviderFilter, number>;
  active: ProviderFilter;
  onPick: (filter: ProviderFilter) => void;
}) {
  const { t } = useTranslation('network');
  return (
    <div
      role="group"
      aria-label={t('providers.filter.label')}
      className="grid grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))] gap-2"
      data-testid="providers-filters"
    >
      {PROVIDER_FILTERS.map((filter) => {
        const pressed = active === filter;
        return (
          <button
            key={filter}
            type="button"
            aria-pressed={pressed}
            onClick={() => onPick(filter)}
            data-testid={`providers-filter-${filter}`}
            className={cn(
              'flex min-h-[3.75rem] flex-col items-start justify-center gap-1 rounded-lg border bg-card px-3 py-2 text-left transition-colors',
              'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              pressed && 'border-primary bg-accent ring-1 ring-primary',
            )}
          >
            <span className="text-2xl font-bold leading-none tabular-nums" data-testid={`providers-filter-count-${filter}`}>
              {counts[filter]}
            </span>{' '}
            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <span aria-hidden className={cn('inline-block h-2 w-2 rounded-full', PILL_DOT[filter])} />
              {t(`providers.filter.${filter}`)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function attentionText(t: TFunction<'network'>, item: AttentionItem): string {
  switch (item.kind) {
    case 'unknown':
      return t('providers.attention.unknown', { service: item.declaredBy });
    case 'flight':
      return t(`providers.attention.flight.${item.state}`);
    case 'offline':
    case 'credentials': {
      const d = item.descriptor;
      const category = canonicalCategory(d.category);
      const values = {
        name: vendorLabel(t, d.category, d.vendor),
        section: t(`providers.integrations.category.${category}`, { defaultValue: category }),
      };
      return item.kind === 'offline'
        ? t('providers.attention.offline', values)
        : t('providers.attention.credentialsMissing', values);
    }
  }
}

/** One amber line, only when something needs attention. */
export function AttentionLine({ items }: { items: AttentionItem[] }) {
  const { t } = useTranslation('network');
  if (items.length === 0) return null;
  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
      data-testid="providers-attention"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <p className="min-w-0">
        <span className="font-semibold">{t('providers.attention.title', { count: items.length })}</span>{' '}
        {items.map((item, i) => (
          <span key={`${item.kind}-${i}`} data-testid="providers-attention-item" data-kind={item.kind}>
            {i > 0 ? ' · ' : ''}
            {attentionText(t, item)}
          </span>
        ))}
      </p>
    </div>
  );
}

/** Nothing matches the active pill. */
export function EmptyFilter({ onClear }: { onClear: () => void }) {
  const { t } = useTranslation('network');
  return (
    <div
      className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground"
      data-testid="providers-filter-empty"
    >
      {t('providers.filter.empty')}{' '}
      <button
        type="button"
        onClick={onClear}
        className="min-h-11 px-1 font-medium text-foreground underline underline-offset-4 hover:text-primary"
        data-testid="providers-filter-show-all"
      >
        {t('providers.filter.showAll')}
      </button>
    </div>
  );
}
